import React, { useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { INVENTORY_FILE_TYPE_IDS } from 'globals.js';

// HOOKS
import { useRecordContext } from 'helpers/getRecordData';

// CORE COMPONENTS
import Form from 'core/form/Form';

// PARTS
import Sidebar from 'parts/sidebar/Sidebar';

//LOCAL COMPONENTS
import Attachment from 'routers/interface/tools/attachment/Attachment';

// LOCAL VARIABLES
import { INVENTORY_SOURCE_IDS } from '../../../options';

// MAIN COMPONENT
const LocationUpdateSidebar = () => {

  const { values } = useFormikContext() || {};
  const { refetchRecord } = useRecordContext() || {};

  const {
    daily_sales_inventory_id: dailySalesInventoryId,
    documentAttachment,
    internet_price,
    inventory_source_id,
    invoice_cost,
    located_at_dealership,
    location_arrival_date,
    location_comments,
    location_type_id,
    new_used,
    retail_price,
    stock_num,
    vehicle_year,
    make,
    model
  } = values;
  const locatedAtDealership = !parseInt(located_at_dealership); // current value is opposite prior to update
  const isNew = !!new_used;
  const isTrade = +inventory_source_id === INVENTORY_SOURCE_IDS.TRADE;
  const documentTypeInvoice = isNew ? INVENTORY_FILE_TYPE_IDS.FACTORY_INVOICE_NEW : INVENTORY_FILE_TYPE_IDS.PURCHASE_INVOICE_USED;
  const documentTypeMSRP = INVENTORY_FILE_TYPE_IDS.MSRP_STICKER_NEW;
  const optionKeys = { label: 'inventorylocation_type_name', value: 'inventorylocation_type_id' };
  const lookupParams = useMemo(
    () => ({
      inventoryLocations: {
        newUsed: isNew,
        vehicleAtDealership: 0
      }
    }),
    [isNew]
  );

  // RENDER
  return (
    <Sidebar
      title="Vehicle Location Update"
      name="locationUpdate"
    >
      <Sidebar.Form
        method="PUT"
        endpoint={ENDPOINTS.inventory.location}
        loadingMessage={`Updating location for stock# ${values.stock_num}`}
        successMessage={`Location updated for stock# ${values.stock_num}`}
        errorMessage={e => {
          return e?.errors?.length
            ? `${e.errors.join('. ')}.`
            : `Failed to update the location for stock# ${values.stock_num}`;
        }}
        initialValues={{
          daily_sales_inventory_id: dailySalesInventoryId,
          documentAttachment: {
            invoice: documentAttachment?.invoice?.length ? documentAttachment.invoice : undefined,
            msrp: documentAttachment?.msrp?.length ? documentAttachment.msrp : undefined
          },
          internet_price,
          invoice_cost,
          located_at_dealership: locatedAtDealership ? 1 : 0,
          location_arrival_date: !locatedAtDealership ? '' : location_arrival_date,
          location_comments: !locatedAtDealership ? '' : location_comments,
          location_type_id: !locatedAtDealership ? '' : location_type_id,
          retail_price
        }}
        onSuccess={() => {
          refetchRecord();
        }}
        body={{ vertical: true }}
        formatParams={v => {
          return {
            ...v,
            location_type_id: !locatedAtDealership ? v.location_type_id : 3
          };
        }}
      >
        {!locatedAtDealership ? (
          <Form.Body>
            <Form.Checklist
              lookup="inventoryLocations"
              type="radio"
              name="location_type_id"
              label="Location"
              optionKeys={optionKeys}
              lookupParams={lookupParams.inventoryLocations}
              useNone
              required
            />
            <Form.Date name="location_arrival_date" label="Estimated Date of Arrival for Physical Check-in" required />
            <Form.Textarea name="location_comments" label="Location Comments" required />
          </Form.Body>
        ) : (
          <Form.Body>
            <Form.Text className="large">
              You are confirming that {`Stock# ${stock_num} ${vehicle_year} ${make} ${model}`} is now physically at the dealership and an E-Post email will be sent to
              Accounting.
            </Form.Text>
            <Form.Control
              name="retail_price"
              label={isNew ? 'MSRP' : 'Retail Price'}
              schema="dollars"
              required={isNew && !isTrade}
            />
            {isNew ? (
              <Attachment.Upload
                label="MSRP Sticker/Window Sticker"
                name="documentAttachment.msrp"
                documentType={documentTypeMSRP}
                uploadType='inventory'
                useTemp
                useDocUpr={false}
              />
            ) : null}
            <Form.Control name="internet_price" label="Internet Price" schema="dollars" />
            <Form.Control
              name="invoice_cost"
              label={isNew ? 'Factory Invoice Cost' : 'ACV'}
              schema="dollars"
              required={!isTrade}
            />
            <Attachment.Upload
              label={isNew ? 'Factory Invoice' : 'Purchase Invoice'}
              name="documentAttachment.invoice"
              documentType={documentTypeInvoice}
              uploadType='inventory'
              useTemp
              useDocUpr={false}
              required={!isTrade}
            />
          </Form.Body>
        )}
      </Sidebar.Form>
    </Sidebar>
  );
};

// EXPORT
export default LocationUpdateSidebar;
